import { MutationOptions, useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

const useDownloadDocument = <T, F>({
  downloadOption,
  createOption,
  downloadParams,
}: {
  downloadOption: () => MutationOptions<
    T & { data: string },
    Error,
    F & { id: string }
  >;
  createOption: () => MutationOptions<
    T & { data: string },
    Error,
    F & { id: string }
  >;
  downloadParams?: F & { id: string };
}) => {
  const { id } = useParams();
  const [isPending, setIsPending] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestRef = useRef<number>(0);
  const { mutate: downloadZip } = useMutation<
    T & { data: string },
    Error,
    F & { id: string }
  >({
    ...downloadOption(),
    onSuccess: async (res: any) => {
      if (res?.data === 'preparing file') {
        setIsPending(true);
        requestRef.current = requestRef.current + 1;
      } else {
        const url = await res.data;
        const a = document.createElement('a');
        a.href = url;
        a.download = 'document.zip';
        a.click();
        window.URL.revokeObjectURL(url);
        setIsPending(false);
      }
    },
  });
  const { mutate: createZip } = useMutation({
    ...createOption(),
    onSuccess: () => {
      downloadZip({ ...downloadParams, id: id! } as F & { id: string });
    },
  });
  useEffect(() => {
    if (isPending) {
      timeoutRef.current = setTimeout(() => {
        downloadZip({ ...downloadParams, id: id! } as F & {
          id: string;
        });
      }, 10000);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        setIsPending(false);
      }
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isPending, requestRef.current]);

  return {
    isPending,
    createZip: (item: F & { id: string }) => createZip(item),
  };
};

export default useDownloadDocument;
